import prepend from 'ramda/src/prepend'
import head from 'ramda/src/head'
import tail from 'ramda/src/tail'
import last from 'ramda/src/last'
import allPass from 'ramda/src/allPass'
import any from 'ramda/src/any'
import pathEq from 'ramda/src/pathEq'
import filter from 'ramda/src/filter'
import find from 'ramda/src/find'
import clone from 'ramda/src/clone'
import isNil from 'ramda/src/isNil'
import BotStateUtils from '@/utils/bot-state.utils'
import { ANSWER_END, INPUT_TYPES } from '@/utils/bot-json.constants'
import { SECTION_FOCUSES, PREVIEW_ITEM } from '@/store/constants'
import { HINT_TEXT, CUSTOMER_TEXT } from '@/utils/text.constants'
import { NODE_TYPES, EDGE_TRANSITION_TYPES } from './state-tree.generator'
import stateTreeUtils from './state-tree.utils'
import answerModule from '@/store/modules/answer.module'
import chatBubbleModule from '@/store/modules/chat-bubble.module'
import conversationModule from '@/store/modules/conversation.module'

// TODO move the contents to different file after we migrate to new ChatPreview
class PreviewUtils {

  findVisibleItems(stateTree, bubbleSelected) {
    if(!bubbleSelected || !bubbleSelected.type || (bubbleSelected.type === PREVIEW_ITEM.BOT_BUBBLE && !bubbleSelected.actionId) || stateTree.type === NODE_TYPES.EMPTY) {
      return [this.createAddWidget(stateTree, {}, false)]
    }

    const node = bubbleSelected.type === PREVIEW_ITEM.CUSTOMER_BUBBLE && bubbleSelected.isFirst ? stateTree : stateTreeUtils.findNode(stateTree, bubbleSelected)

    if(!node) {
      console.error('bubbleSelected not found in tree ' + JSON.stringify(bubbleSelected))
      throw new Error('bubbleSelected not found in tree ' + JSON.stringify(bubbleSelected))
    }

    const p = this._createPrecedingItems(node, [], bubbleSelected.inNestedCondition)
    
    // When inNestedCondition is true, we need to find the innermost condition node to get succeeding items
    let succeedingNode = node
    if(bubbleSelected.inNestedCondition) {
      const conditionNode = this._findInnermostConditionNode(node)
      if(conditionNode) {
        succeedingNode = conditionNode
      }
    }
    
    const s = this._createSucceedingItems(succeedingNode, bubbleSelected, [])

    const items = p.concat(tail(s))

    // if we find the outsideConditionNode, it means bubbleSelected is inside condition,
    // we need to create preview items for nodes before and after the condition
    if(head(items).outsideConditionNode) {
      const outsideP = this._createPrecedingItems(head(items).outsideConditionNode)
      const outsideS = this._createSucceedingItems(head(items).outsideConditionNode)
      last(outsideP).caseItems = tail(items)
      last(outsideP).isHighlighted = true
      return outsideP.concat(tail(outsideS))
    }

    return items
  }

  _findInnermostConditionNode(node) {
    // Find the first (innermost) condition node that contains this node
    let current = node
    while(current.parent) {
      if(current.parent.transition.type === EDGE_TRANSITION_TYPES.CONDITION) {
        // Return the condition node itself (not the end node)
        return current.parent.node
      }
      current = current.parent.node
    }
    return null
  }

  generateVisibleItemsForEmpty(stateTree) {
    const isAnswerEndFindIntent = answerModule.getAnswerEndType() === ANSWER_END.FIND_INTENT
    const nodeType = isAnswerEndFindIntent ? NODE_TYPES.FIND_INTENT : NODE_TYPES.IDLE
    const inputType = isAnswerEndFindIntent ? INPUT_TYPES.USER_INPUT : INPUT_TYPES.NONE
    return [
      this.createAddWidget(stateTree, {}, false),
      this.createAnswerEnd(stateTree.stateId, nodeType, false, false, null, false, false, inputType)
    ]
  }

  _createPrecedingItems(node, items = [], inNestedCondition = false) {
    const parent = node.parent
    if(!parent) {
      return prepend(this.createBubbleFromNode(node), items)
    }
    switch(parent.transition.type) {
      case EDGE_TRANSITION_TYPES.NONE:
        if([NODE_TYPES.IDLE, NODE_TYPES.CONTINUE_TEMPLATE, NODE_TYPES.CONTINUE_CONDITION, NODE_TYPES.CONTINUE_ANSWER].includes(node.type)) {
          if(node.isTemplate && node.isCondition) {
            if(inNestedCondition) {
              return this._createPrecedingItems(parent.node, items, inNestedCondition)
            }
            const answerEndItems = this.isAnswerEndHidden(parent.node.stateId, true) ? [] : [this.createAnswerEnd(parent.node.stateId, node.type, true, true, node.conditionNode.conditionEndStateId, node.isPostback, false, parent.transition.type, node.answerId)]
            return this._createPrecedingItems(parent.node, answerEndItems)
          }

          return this._createPrecedingItems(parent.node, [
            this.createAddWidget(parent.node, this._resolveAddWidgetParams(node), !!node.isCondition)
          ].concat(items), inNestedCondition)
        }
        if(inNestedCondition) {
          return this._createPrecedingItems(parent.node, items, inNestedCondition)
        }
        return this._createPrecedingItems(parent.node, prepend(this.createBubbleFromNode(node), items))
      case EDGE_TRANSITION_TYPES.USER_INPUT:
        if([NODE_TYPES.FIND_INTENT, NODE_TYPES.CONTINUE_OR_FIND_INTENT, NODE_TYPES.IDLE].includes(node.type)) {
          if(node.isTemplate && node.isCondition) {
            const items = this.isAnswerEndHidden(parent.node.stateId, true) ? [] : [this.createAnswerEnd(parent.node.stateId, node.type, true, true, node.conditionNode.conditionEndStateId, node.isPostback, node.isQuickReply, parent.transition.type)]
            return this._createPrecedingItems(parent.node, items, inNestedCondition)
          }

          return this._createPrecedingItems(parent.node, [
            this.createAddWidget(parent.node, this._resolveAddWidgetParams(node), !!node.isCondition)
          ].concat(items), inNestedCondition)
        }

        const validationActionId = parent.node.type === NODE_TYPES.VALIDATION ? parent.node.actionId : null
        if(node.type === NODE_TYPES.SAVE_CONTEXT) {
          const nextStateId = head(node.children).node.stateId
          const customerBubbleText = this._resolveWaitForInputCustomerBubbleText(node.stateId, node.isTemplate)
          if(inNestedCondition) {
            return this._createPrecedingItems(parent.node, items, inNestedCondition)
          }
          return this._createPrecedingItems(parent.node, [
            this.createWaitForInput(parent.node.stateId, nextStateId, validationActionId, node.isTemplate, node.stateId),
            this.createCustomerBubble(customerBubbleText)
          ].concat(items), inNestedCondition)
        }
        if(inNestedCondition) {
          return this._createPrecedingItems(parent.node, items, inNestedCondition)
        }
        return this._createPrecedingItems(parent.node, [
          this.createWaitForInput(parent.node.stateId, node.stateId, validationActionId, node.isTemplate, null),
          this.createCustomerBubble(CUSTOMER_TEXT.WAIT_FOR_INPUT),
          this.createBubbleFromNode(node)
        ].concat(items), inNestedCondition)
      case EDGE_TRANSITION_TYPES.POSTBACK:
      {
        if(inNestedCondition) {
          return this._createPrecedingItems(parent.node, items, inNestedCondition)
        }

        if([NODE_TYPES.CONTINUE_ANSWER, NODE_TYPES.IDLE].includes(node.type)) {
          const successors = [
            this.createAddWidget(parent.node, { disabled: false, disabledHint: HINT_TEXT.QUICK_REPLIES }, false),
            this.createAnswerEnd(parent.node.stateId, last(parent.node.children)?.node.type, !!parent.node.isTemplate, false, null, last(parent.node.children)?.transition.type === EDGE_TRANSITION_TYPES.POSTBACK, false, INPUT_TYPES.NONE)
          ]
          const addWidgetStateId = node.parent.transition.type === EDGE_TRANSITION_TYPES.POSTBACK ? node.parent.transition.postback : node.stateId || BotStateUtils.generateId('qr')
          return this._createPrecedingItems(parent.node,
            successors.concat(
              this.createCustomerBubble(parent.transition.title),
              this.createAddWidget({ ...node, stateId: addWidgetStateId }, { disabled: false, disabledHint: HINT_TEXT.QUICK_REPLIES }, !!node.isCondition),
              items
            ),
            inNestedCondition
          )
        }

        const successors = tail(this._createSucceedingItems(parent.node, null))
        return this._createPrecedingItems(parent.node,
          successors.concat(
            this.createCustomerBubble(parent.transition.title),
            this.createBubbleFromNode(node),
            items
          ),
          inNestedCondition
        )
      }
      case EDGE_TRANSITION_TYPES.QUICK_REPLY:
      {
        if(inNestedCondition) {
          return this._createPrecedingItems(parent.node, items, inNestedCondition)
        }

        if([NODE_TYPES.CONTINUE_ANSWER, NODE_TYPES.IDLE].includes(node.type)) {
          const addWidget = this.createAddWidget(clone(parent.node), { disabled: true, disabledHint: HINT_TEXT.QUICK_REPLIES }, false)
          const lastStateTransition = last(conversationModule.getStateTransitions(parent.node.stateId))
          const successors = [
            addWidget,
            this.createAnswerEnd(parent.node.stateId, last(parent.node.children).node.type, !!parent.node.isTemplate, false, null, false, true, lastStateTransition.condition.input, node.answerId)
          ]
          return this._createPrecedingItems(parent.node,
            successors.concat(
              this.createCustomerBubble(parent.transition.title),
              this.createAddWidget({ ...node, stateId: node.parent.transition.payload }, { disabled: false, disabledHint: HINT_TEXT.QUICK_REPLIES }, !!node.isCondition),
              items
            ),
            inNestedCondition
          )
        }

        const successors = tail(this._createSucceedingItems(parent.node, null))
        return this._createPrecedingItems(parent.node,
          successors.concat(
            this.createCustomerBubble(parent.transition.title),
            this.createBubbleFromNode(node),
            items
          ),
          inNestedCondition
        )
      }
      case EDGE_TRANSITION_TYPES.CONDITION:
        if(!node.isTemplate && [NODE_TYPES.IDLE, NODE_TYPES.FIND_INTENT, NODE_TYPES.CONTINUE_OR_FIND_INTENT, NODE_TYPES.CONTINUE_TEMPLATE, NODE_TYPES.CONTINUE_CONDITION, NODE_TYPES.CONTINUE_ANSWER].includes(node.type)) {
          return [
            { outsideConditionNode: parent.node },
            this.createAddWidget(parent.node, this._resolveAddWidgetParams(node), !!node.isCondition)
          ].concat(items)
        }

        if(inNestedCondition) {
          // We've reached the condition boundary, just continue up without creating the condition section
          // (it's already being created in succeeding items)
          return this._createPrecedingItems(parent.node, items)
        }

        // this is a bit of a hack, we are prepending a fake node in case we have started inside a condition
        return [{ outsideConditionNode: parent.node }, this.createBubbleFromNode(node)].concat(items)
      default:
        throw new Error('unknown transition type: ' + parent.transition.type)
    }
  }

  _createSucceedingItems(node, bubbleSelected, items = []) {
    // answer ends
    if([NODE_TYPES.IDLE, NODE_TYPES.FIND_INTENT, NODE_TYPES.CONTINUE_OR_FIND_INTENT, NODE_TYPES.CONTINUE_TEMPLATE, NODE_TYPES.CONTINUE_CONDITION, NODE_TYPES.CONTINUE_ANSWER].includes(node.type)) {
      const parentStateId = node.parent.node.stateId
      if(node.isTemplate && node.isCondition) {
        return this.isAnswerEndHidden(parentStateId, true) ? items : items.concat(
          this.createAnswerEnd(parentStateId, node.type, true, true, node.conditionNode.conditionEndStateId, node.isPostback, node.isQuickReply, node.parent.transition.type, node.answerId)
        )
      }
      const isPostback = node.parent.transition.type === EDGE_TRANSITION_TYPES.POSTBACK
      const isQuickReply = node.parent.transition.type === EDGE_TRANSITION_TYPES.QUICK_REPLY
      const addWidgetNode = isPostback || isQuickReply ? node : node.parent.node
      const addWidget = this.createAddWidget(clone(addWidgetNode), this._resolveAddWidgetParams(node), !!node.isCondition)
      const answerEndStateId = () => {
        if (isPostback) return node.parent.transition.postback
        if (isQuickReply) return node.parent.transition.payload
        return parentStateId
      }
      if (isPostback) addWidget.node.stateId = node.parent.transition.postback
      if (isQuickReply) addWidget.node.stateId = node.parent.transition.payload
      return items.concat(
        addWidget,
        this.createAnswerEnd(answerEndStateId(), node.type, !!node.isTemplate, !!node.isCondition, node.conditionNode ? node.conditionNode.conditionEndStateId : null, node.isPostback, node.isQuickReply, node.parent.transition.type, node.answerId)
      )
    }

    if(node.type === NODE_TYPES.EMPTY) {
      return items.concat(
        this.createAddWidget(node, {}, !!node.isCondition)
      )
    }

    // first, process non-button/qr transitions
    const nodeItems = items.concat(node.type === NODE_TYPES.SAVE_CONTEXT ? [] : this.createBubbleFromNode(node))
    const successors = this._processNodeChildren(node, nodeItems)

    // button/qr transitions are displayed last
    const sectionFocus = bubbleSelected?.sectionFocus
    if(sectionFocus) {
      if(sectionFocus.startsWith(SECTION_FOCUSES.QUICK_REPLY)) {
        const index = this.getIndexFromFocus(sectionFocus, SECTION_FOCUSES.QUICK_REPLY)
        const quickReplies = filter(pathEq(['transition', 'type'], EDGE_TRANSITION_TYPES.QUICK_REPLY), node.children)
        return this._createSucceedingItems(quickReplies[index].node, null, successors.concat(
          this.createCustomerBubble(quickReplies[index].transition.title)
        )
        )
      }

      if(sectionFocus.startsWith(SECTION_FOCUSES.BUTTON)) {
        const btnIndex = this.getIndexFromFocus(sectionFocus, SECTION_FOCUSES.BUTTON)
        const postback = find(allPass(
          [
            pathEq(['transition', 'type'], EDGE_TRANSITION_TYPES.POSTBACK),
            pathEq(['transition', 'index'], btnIndex)
          ]
        ), node.children)

        if(postback) {
          return this._createSucceedingItems(postback.node, null, successors.concat(
            this.createCustomerBubble(postback.transition.title)
          ))
        }
      }
    }

    return successors
  }

  _findConditionEndNode(conditionNode) {
    // Traverse through all cases to find the end node
    // For now, look for the first child with NONE transition after the condition
    for(const child of conditionNode.children) {
      if(child.transition.type === EDGE_TRANSITION_TYPES.NONE) {
        return child.node
      }
    }
    return null
  }

  _processNodeChildren(node, items) {
    for(const childEdge of node.children) {
      const isWaitThenIdle = childEdge.transition.type === EDGE_TRANSITION_TYPES.USER_INPUT && childEdge.node.type === NODE_TYPES.IDLE
      if(childEdge.transition.type === EDGE_TRANSITION_TYPES.NONE || isWaitThenIdle) {
        return items.concat(this._createSucceedingItems(childEdge.node, null, []))
      }

      if(childEdge.transition.type === EDGE_TRANSITION_TYPES.USER_INPUT && !isWaitThenIdle) {
        // find intent is a special case where we don't want to display the wait for input bubble
        const newItems = clone(items)
        if(![NODE_TYPES.FIND_INTENT, NODE_TYPES.CONTINUE_OR_FIND_INTENT].includes(childEdge.node.type)) {
          const isChildSaveContext = childEdge.node.type === NODE_TYPES.SAVE_CONTEXT
          let nextStateId = childEdge.node.stateId
          if (isChildSaveContext) nextStateId = head(childEdge.node.children).node.stateId

          const contextStateId = isChildSaveContext ? childEdge.node.stateId : null
          const customerBubbleText = this._resolveWaitForInputCustomerBubbleText(contextStateId, childEdge.node.isTemplate)
          const validationActionId = node.type === NODE_TYPES.VALIDATION ? node.actionId : null
          newItems.push(
            this.createWaitForInput(node.stateId, nextStateId, validationActionId, node.isTemplate, contextStateId),
            this.createCustomerBubble(customerBubbleText, false, contextStateId)
          )
        }
        return newItems.concat(this._createSucceedingItems(childEdge.node, null, []))
      }
    }
    return items
  }

  _resolveWaitForInputCustomerBubbleText(contextStateId, isTemplate) {
    if (!contextStateId) return CUSTOMER_TEXT.WAIT_FOR_INPUT

    const contextStateGetter = isTemplate ? 'getTemplateState' : 'getState'
    const { contextKey = '${custom-name}' } = conversationModule[contextStateGetter](contextStateId) || {}
    return CUSTOMER_TEXT.WAIT_FOR_VALIDATED_INPUT(contextKey)
  }

  _nodeHasQr(node) {
    return any(pathEq(['transition', 'type'], EDGE_TRANSITION_TYPES.QUICK_REPLY), node.children)
  }

  _resolveAddWidgetParams(answerEndNode) {
    if(answerEndNode.isTemplate) {
      return { disabled: true, disabledHint: HINT_TEXT.BLOCK }
    }
    if(answerEndNode.isCondition) {
      return { disabled: this._nodeHasQr(answerEndNode.parent.node), disabledHint: HINT_TEXT.QUICK_REPLIES, disableCondition: true }
    }
    return { disabled: answerEndNode.parent.transition.type === EDGE_TRANSITION_TYPES.QUICK_REPLY ? false : this._nodeHasQr(answerEndNode.parent.node), disabledHint: HINT_TEXT.QUICK_REPLIES }
  }

  createBubbleFromNode(node) {
    switch(node.type) {
      case NODE_TYPES.OUTPUT: return this.createBotBubble(node)
      case NODE_TYPES.REMOTE: return this.createRemoteBubble(node)
      case NODE_TYPES.CHAT_GPT: return this.createChatGPTBubble(node)
      case NODE_TYPES.CONDITION:
      {
        const selectedCase = chatBubbleModule.getConditionCaseSelected(node.stateId)
        const items = this._createSucceedingItems(node.cases[selectedCase].node)
        return this.createConditionSection(node, selectedCase, items)
      }
      case NODE_TYPES.IDLE:
      case NODE_TYPES.FIND_INTENT:
      case NODE_TYPES.CONTINUE_OR_FIND_INTENT:
      case NODE_TYPES.CONTINUE_TEMPLATE:
      case NODE_TYPES.CONTINUE_CONDITION:
      case NODE_TYPES.CONTINUE_ANSWER:
        return this.createAnswerEnd(node.stateId, node.type, !!node.isTemplate, !!node.isCondition, node.conditionNode ? node.conditionNode.conditionEndStateId : null, node.isPostback, node.isQuickReply, node.parent.transition.type, node.answerId)
      default: return [] // not sure about this but at least in concat it would effectively skip the bubble
    }
  }

  createConditionSection(node, selectedCase, items) {
    return {
      type: PREVIEW_ITEM.CONDITION_SECTION,
      node: node,
      caseSelected: selectedCase,
      caseItems: items,
      isHighlighted: false
    }
  }

  createBotBubble(node) {
    return {
      type: PREVIEW_ITEM.BOT_BUBBLE,
      node
    }
  }

  createRemoteBubble(node) {
    return {
      type: PREVIEW_ITEM.REMOTE_BUBBLE,
      node
    }
  }

  createChatGPTBubble(node) {
    return {
      type: PREVIEW_ITEM.CHAT_GPT_BUBBLE,
      node
    }
  }

  createCustomerBubble(text, isFirst = false) {
    return {
      type: PREVIEW_ITEM.CUSTOMER_BUBBLE,
      isFirst, text
    }
  }

  createAnswerEnd(stateId, value, isTemplate, isCondition, conditionEndStateId, isPostback, isQuickReply, inputType, answerId) {
    return {
      type: PREVIEW_ITEM.ANSWER_END,
      stateId, value: stateTreeUtils.resolveAnswerEndValue(value, inputType), isTemplate, isCondition, conditionEndStateId, isPostback, isQuickReply, inputType, answerId
    }
  }

  createAddWidget(node, params, isCondition) {
    return {
      type: PREVIEW_ITEM.ADD_WIDGET,
      node, params, isCondition
    }
  }

  createWaitForInput(stateId, nextStateId, validationActionId, isTemplate, contextStateId) {
    return {
      type: PREVIEW_ITEM.WAIT_FOR_INPUT,
      stateId, nextStateId, isTemplate, contextStateId, validationActionId
    }
  }

  isAnswerEndHidden(stateId, isCondition) {
    return isCondition &&
      conversationModule.isConditionState(stateId) &&
      chatBubbleModule.getConditionCaseSelected(stateId) !== conversationModule.getStateTransitions(stateId).length - 1
  }

  getIndexFromFocus(focus, focusType) {
    return Number(focus.replace(focusType, ''))
  }

  getSectionFocusFromTransition(transition) {
    if (transition) {
      const sectionFocusIndex = isNil(transition.index) ? '' : transition.index
      if (transition.type === EDGE_TRANSITION_TYPES.POSTBACK) {
        return SECTION_FOCUSES.BUTTON + sectionFocusIndex
      }
      if (transition.type === EDGE_TRANSITION_TYPES.QUICK_REPLY) {
        return SECTION_FOCUSES.QUICK_REPLY + sectionFocusIndex
      }
    }
    return SECTION_FOCUSES.NONE
  }
}

export default new PreviewUtils()
